// @ts-check
// Carga dos dados de cada widget (em paralelo, um pedido por cartão), com cache curto para que a grade e a
// pré-visualização do editor dividam o mesmo pedido, e conversão dos dados em CSV. Escritas desta aba e as que a
// atualização automática traz de outras pessoas (mutationCounterState) fazem os cartões relerem sem esqueleto: o número
// clicado não fica para trás da lista que ele abre.
import { useEffect, useRef, useState } from 'preact/hooks';
import { fetchWidgetData } from '../../data/dashboards.js';
import { mutationCounterState } from '../../data/record-store.js';
import { buildCsv, formatCsvNumber } from '../../lib/csv.js';
import { formatCellValue } from './widget-format.js';
import { getStatusLabel } from './simple-widgets.js';
import { bucketText, formatReportDetails, isReportWidgetType } from './report-widgets.js';
import { describeStatSecondary, statLabelText } from './report-stat-parts.js';
import { t } from '../../i18n/index.js';
import { checkIframeUrlForThisApp } from './iframe-check.js';
import { geoCsvRows } from '../../lib/geo-map.js';
import { BRAZIL_STATES } from '../../../../shared/geo/brazil-states.js';
import { isLeadQualityWithoutMeasures } from '../../../../shared/dashboards/lead-quality-cards.js';

/** @param {unknown} url */
const isIframeUrlUsable = (url) => checkIframeUrlForThisApp(url).isValid;

/**
 * @typedef {import('../../data/dashboards.js').WidgetData} WidgetData
 * @typedef {import('../../data/dashboards.js').DashboardWidget} DashboardWidget
 * @typedef {import('../../data/dashboards.js').DashboardRequestFilters} DashboardRequestFilters
 */

const CACHE_TIME_TO_LIVE_MILLISECONDS = 15_000;
/** @type {Map<string, { promise: Promise<WidgetData>, createdAt: number }>} */
const responseCache = new Map();

/**
 * @param {{ type: string, configuration: Record<string, any> }} widget
 * @param {DashboardRequestFilters} filters
 */
function buildCacheKey(widget, filters) {
  return JSON.stringify([widget.type, widget.configuration, filters]);
}

/**
 * @param {{ type: any, configuration: Record<string, any> }} widget
 * @param {DashboardRequestFilters} filters
 * @param {{ force?: boolean }} [options]
 */
function loadWidgetData(widget, filters, { force = false } = {}) {
  const key = buildCacheKey(widget, filters);
  const cached = responseCache.get(key);
  if (!force && cached && Date.now() - cached.createdAt < CACHE_TIME_TO_LIVE_MILLISECONDS) return cached.promise;
  const promise = fetchWidgetData(widget, filters);
  responseCache.set(key, { promise, createdAt: Date.now() });
  promise.catch(() => responseCache.delete(key));
  if (responseCache.size > 200) responseCache.delete(responseCache.keys().next().value);
  return promise;
}

/** Esquece respostas guardadas (ex.: depois de salvar a grade). */
export function clearWidgetDataCache() {
  responseCache.clear();
}

/**
 * Configuração mínima para pedir dados (equivalente ao hasMinimalRequiredConfigForGraph do Twenty).
 * @param {{ type: string, configuration: Record<string, any> }} widget
 */
export function isWidgetConfigurationComplete(widget) {
  const configuration = widget.configuration ?? {};
  if (isReportWidgetType(widget.type)) return Boolean(configuration.report);
  switch (widget.type) {
    case 'TEXT':
      return true;
    case 'IFRAME':
      return isIframeUrlUsable(configuration.url);
    case 'KPI':
      return Boolean(configuration.metric);
    case 'AGGREGATE':
      return Boolean(configuration.objectName && configuration.operation && (configuration.operation === 'COUNT' || configuration.fieldName));
    case 'BAR':
    case 'LINE':
    case 'PIE':
      return Boolean(configuration.objectName && configuration.operation && configuration.groupByFieldName && (configuration.operation === 'COUNT' || configuration.fieldName));
    case 'TABLE':
      return Boolean(configuration.objectName && Array.isArray(configuration.fields) && configuration.fields.length > 0);
    default:
      return false;
  }
}

/**
 * @param {WidgetData | null} data
 */
export function isWidgetDataEmpty(data) {
  if (!data) return true;
  switch (data.kind) {
    case 'SERIES':
      return data.categories.length === 0 || data.series.every((series) => series.values.every((value) => !value));
    case 'PIE':
      return data.slices.every((slice) => !Number(slice.value));
    case 'TABLE':
      return data.rows.length === 0;
    case 'TEXT':
      return !String(data.markdown ?? '').trim();
    case 'BAR_LIST':
      return (data.items.length === 0 && (data.stats ?? []).every((stat) => !stat.value)) || isLeadQualityWithoutMeasures(data);
    case 'DISTRIBUTION':
      return data.total === 0 || isLeadQualityWithoutMeasures(data);
    case 'FUNNEL_FLOW':
      return !data.tabs?.ALL?.total;
    case 'STAGE_FLOW':
      return data.columns.every((column) => !column.inside.count && !column.entered.count && !column.lost.count);
    case 'GEO_MAP':
      // sem leads no período não há o que mapear; com leads sem CEP, o cartão mostra a qualidade dos dados
      return data.quality.total === 0;
    default:
      return false;
  }
}

/**
 * @param {{ type: any, configuration: Record<string, any> }} widget
 * @param {DashboardRequestFilters} filters
 */
export function useWidgetData(widget, filters) {
  const key = buildCacheKey(widget, filters);
  const isComplete = isWidgetConfigurationComplete(widget);
  const [state, setState] = useState(/** @type {{ status: 'loading' | 'ready' | 'error', data: WidgetData | null, error: unknown }} */ ({ status: 'loading', data: null, error: null }));
  const [reloadCount, setReloadCount] = useState(0);
  // registros mudaram (aqui ou noutra aba, pela atualização automática): relê sem o cache curto
  const mutationCount = mutationCounterState.value;
  const loadedMutationCountRef = useRef(mutationCount);

  useEffect(() => {
    if (!isComplete) return undefined;
    // TEXT e IFRAME não dependem do servidor: o texto e o endereço já estão na configuração.
    if (widget.type === 'TEXT') {
      setState({ status: 'ready', data: { kind: 'TEXT', markdown: widget.configuration?.markdown ?? '' }, error: null });
      return undefined;
    }
    if (widget.type === 'IFRAME') {
      setState({ status: 'ready', data: { kind: 'IFRAME', url: widget.configuration?.url ?? null }, error: null });
      return undefined;
    }
    let isCancelled = false;
    const isAfterMutation = loadedMutationCountRef.current !== mutationCount;
    loadedMutationCountRef.current = mutationCount;
    setState((current) => ({ ...current, status: 'loading' }));
    loadWidgetData(widget, filters, { force: reloadCount > 0 || isAfterMutation })
      .then((data) => {
        if (!isCancelled) setState({ status: 'ready', data, error: null });
      })
      .catch((error) => {
        if (!isCancelled) setState({ status: 'error', data: null, error });
      });
    return () => {
      isCancelled = true;
    };
  }, [key, reloadCount, isComplete, mutationCount]);

  return { ...state, isComplete, reload: () => setReloadCount((count) => count + 1) };
}

/**
 * Linhas da planilha a partir dos dados exibidos (gerada no navegador).
 * @param {WidgetData} data
 * @returns {string}
 */
export function widgetDataToCsv(data) {
  switch (data.kind) {
    case 'NUMBER': {
      const rows = [[t('dashboards.csv.indicator'), t('dashboards.csv.value')], [t('dashboards.csv.current'), formatCsvNumber(data.value)]];
      if (data.previous !== null && data.previous !== undefined) rows.push([t('dashboards.csv.previousYear'), formatCsvNumber(data.previous)]);
      if (data.goal !== null && data.goal !== undefined) rows.push([t('dashboards.csv.goal'), formatCsvNumber(data.goal)]);
      if (data.secondary) rows.push([data.secondary.label, typeof data.secondary.value === 'number' ? formatCsvNumber(data.secondary.value) : data.secondary.value ?? '']);
      return buildCsv(rows);
    }
    case 'SERIES':
      return buildCsv([
        [t('dashboards.csv.category'), ...data.series.map((series) => series.label)],
        ...data.categories.map((category, index) => [category.label, ...data.series.map((series) => formatCsvNumber(series.values[index]))]),
      ]);
    case 'PIE':
      return buildCsv([
        [t('dashboards.csv.category'), t('dashboards.csv.value')],
        ...data.slices.map((slice) => [slice.label, formatCsvNumber(slice.value)]),
        [t('dashboards.chart.total'), formatCsvNumber(data.total)],
      ]);
    case 'TABLE': {
      const toCell = (/** @type {any} */ value, /** @type {string | undefined} */ format) => {
        if (format === 'STATUS') return value ? getStatusLabel(String(value)) : '';
        if ((format === 'NUMBER' || format === 'CURRENCY' || format === 'PERCENT') && value !== '' && value !== null && !Number.isNaN(Number(value))) return formatCsvNumber(Number(value));
        return formatCellValue(value, /** @type {any} */ (format));
      };
      const rows = [
        data.columns.map((column) => column.label),
        ...data.rows.map((row) => data.columns.map((column) => toCell(row.cells?.[column.key], column.format))),
      ];
      if (data.totals) rows.push(data.columns.map((column, index) => (index === 0 && data.totals?.[column.key] == null ? t('dashboards.chart.total') : toCell(data.totals?.[column.key], column.format))));
      return buildCsv(rows);
    }
    case 'STAT_GROUP':
      // grupo de números v2 (contrato 07, seção 7.1): seta, meta e legenda secundária em colunas próprias
      return buildCsv([
        [t('reports.csv.indicator'), t('reports.csv.value'), t('reports.csv.previousYear'), t('reports.csv.goal'), t('reports.csv.goalProgress'), t('reports.csv.detail')],
        ...data.stats.map((stat) => [
          statLabelText(stat),
          formatCsvNumber(stat.value),
          stat.previous === null || stat.previous === undefined ? '' : formatCsvNumber(stat.previous),
          stat.goal ? formatCsvNumber(stat.goal.value) : '',
          stat.goal ? formatCsvNumber(stat.goal.progress) : '',
          describeStatSecondary(stat) ?? '',
        ]),
      ]);
    case 'BAR_LIST':
      return buildCsv([
        [t('reports.csv.item'), t('reports.csv.value'), t('reports.csv.details')],
        ...(data.stats ?? []).map((stat) => [t(`reports.stats.${stat.key}`), formatCsvNumber(stat.value), '']),
        ...data.items.map((item) => [item.label, formatCsvNumber(item.value), formatReportDetails(item.details)]),
      ]);
    case 'DISTRIBUTION':
      return buildCsv([
        [t('reports.csv.bucket'), t('reports.csv.value'), t('reports.csv.share')],
        ...data.buckets.map((bucket) => [bucketText(bucket, data.unit), formatCsvNumber(bucket.value), formatCsvNumber(bucket.share)]),
        ...(data.stats ?? []).map((stat) => [t(`reports.stats.${stat.key}`), formatCsvNumber(stat.value), '']),
      ]);
    case 'FUNNEL_FLOW': {
      const tab = data.tabs.ALL;
      return buildCsv([
        [t('reports.csv.stage'), t('reports.csv.reached'), t('reports.csv.reachedAmount'), t('reports.csv.share'), t('reports.csv.averageDays')],
        ...tab.rows.map((row) => [row.label, formatCsvNumber(row.reached), formatCsvNumber(row.reachedAmount), formatCsvNumber(row.share), formatCsvNumber(row.averageDays)]),
        [tab.won.label ?? t('reports.funnel.won'), formatCsvNumber(tab.won.count), formatCsvNumber(tab.won.amount), formatCsvNumber(tab.won.share ?? 0), ''],
        [tab.lost.label ?? t('reports.funnel.lost'), formatCsvNumber(tab.lost.count), formatCsvNumber(tab.lost.amount), formatCsvNumber(tab.lost.share ?? 0), ''],
        [t('reports.funnel.lifecycle'), formatCsvNumber(tab.lifecycleDays), '', '', ''],
      ]);
    }
    case 'STAGE_FLOW':
      return buildCsv([
        [t('reports.csv.stage'), t('reports.csv.insideCount'), t('reports.csv.insideAmount'), t('reports.csv.enteredCount'), t('reports.csv.enteredAmount'),
          t('reports.csv.lostCount'), t('reports.csv.lostAmount')],
        ...data.columns.map((column) => [column.label, ...[column.inside, column.entered, column.lost].flatMap((values) => [formatCsvNumber(values.count), formatCsvNumber(values.amount)])]),
      ]);
    case 'GEO_MAP':
      return buildCsv([
        ['level', 'area', 'parent', 'leads', 'withVisit', 'enrollments', 'visitRate', 'conversionRate'].map((key) => t(`geoMap.csv.${key}`)),
        ...geoCsvRows(data, {
          level: (level) => t(`geoMap.levels.${level}`),
          unnamed: t('geoMap.unnamedNeighborhood'),
          stateOf: (code) => BRAZIL_STATES.find((state) => state.code === code)?.name ?? code,
        }).map((row) => row.map((cell) => (typeof cell === 'number' ? formatCsvNumber(cell) : cell ?? ''))),
      ]);
    case 'TEXT':
    default:
      return buildCsv([[/** @type {any} */ (data).markdown ?? '']]);
  }
}
